import React, { useMemo, useState } from 'react';

import { useIntl } from 'react-intl';
import { graphql, useFragment } from 'react-relay';

import { cssMap } from '@atlaskit/css';
import Popup from '@atlaskit/popup';
import { Box, Pressable, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';
import { getFrontendExperiment } from '@atlassian/search-experiment/frontend';
import { useCopyToClipboard } from '@atlassian/search-ui/actions';
import { PersonCard, type PersonCardProps } from '@atlassian/search-ui/card';

import { MessageBody } from '../../../../../../../../../common/ui/message-body';
import { ProfileAvatar } from '../../../../../../../../../common/ui/profile-avatar';
import { getExternalMessageDmIntegrationInfo } from '../../../../../../../../../common/utils/search-result-utils';

import type { threadMessage_AtlassianSearchPage$key } from './__generated__/threadMessage_AtlassianSearchPage.graphql';
import { i18n } from './messages';
import { useExternalPersonFallbackAnalytics } from './use-external-person-fallback-analytics';

interface ThreadMessageProps {
	parent: Parent;
	node: threadMessage_AtlassianSearchPage$key;
}

type Parent = {
	integrationId?: string | null;
} | null;

type User =
	| {
			email: string | null | undefined;
			extendedProfile:
				| {
						jobTitle: string | null | undefined;
				  }
				| null
				| undefined;
			externalId: string;
			name: string | null | undefined;
			nickname?: string | null | undefined;
			picture: string | null | undefined;
	  }
	| null
	| undefined;

const styles = cssMap({
	threadItem: {
		paddingBottom: token('space.050'),
		paddingTop: token('space.050'),
		display: 'block',
		verticalAlign: 'bottom',
	},
	nameContainer: {
		whiteSpace: 'nowrap',
	},
	popupTrigger: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginRight: token('space.075'),
		gap: token('space.025'),
		backgroundColor: 'transparent',
		'&:hover': {
			textDecoration: 'underline',
		},
		font: token('font.body'),
		verticalAlign: 'bottom',
	},
	regularUser: {
		display: 'inline-flex',
		alignItems: 'center',
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),
		marginRight: token('space.075'),
		gap: token('space.025'),
		backgroundColor: 'transparent',
		verticalAlign: 'bottom',
	},
});

const { serveExperiment: serveDoubleLineRenderingModifications } = getFrontendExperiment({
	abTestId: 'slack_result_rendering',
});

export const ThreadMessage = ({ parent, node }: ThreadMessageProps): React.JSX.Element => {
	const [isPersonPopupOpen, setIsPersonPopupOpen] = useState(false);
	const { formatMessage } = useIntl();

	const data = useFragment(
		graphql`
			fragment threadMessage_AtlassianSearchPage on SearchResultGraphDocument {
				...messageBody_AtlassianSearchPage
				integrationId
				entity {
					... on ExternalMessage {
						createdBy {
							thirdPartyUser {
								name
								email
								picture
								externalId
								extendedProfile {
									jobTitle
								}
							}
						}
					}
				}
				initialContributors {
					name
					email
					picture
					externalId
					extendedProfile {
						jobTitle
					}
				}
				allContributors {
					name
					nickname
					email
					picture
					externalId
					extendedProfile {
						jobTitle
					}
				}
			}
		`,
		node,
	);

	const integrationId = parent?.integrationId || data?.integrationId;

	// Check for user with email priority:
	// createdBy?.thirdPartyUser -> initialContributors with email -> allContributors
	let user: User = data?.entity?.createdBy?.thirdPartyUser;
	if (!user) {
		const initialContributor = data?.initialContributors?.[0];
		if (initialContributor?.email) {
			user = initialContributor;
		} else {
			user = data?.allContributors?.[0];
		}
	}

	useExternalPersonFallbackAnalytics({
		externalPersonFallback: !user?.name && !user?.nickname,
		integrationId,
		hasThirdPartyUser: Boolean(data?.entity?.createdBy?.thirdPartyUser),
		hasInitialContributors: Boolean(data?.initialContributors?.length),
		hasAllContributors: Boolean(data?.allContributors?.length),
	});

	const currentMessageOwnerName =
		user?.name || user?.nickname || formatMessage(i18n.externalPersonLabel);
	const picture = user?.picture;

	// We are waiting for this commit ticket to complete for jobTitle & profileUrl
	// https://hello.jira.atlassian.cloud/browse/COMMIT-14774
	type UserData = PersonCardProps['user'];
	const userData: UserData | null =
		user && user.name
			? {
					name: user.name,
					profilePicUrl: user.picture ?? undefined,
					jobTitle: user.extendedProfile?.jobTitle,
					profileUrl: null, // not available as of late Oct 2024
			  }
			: null;

	const onCopyEmail = useCopyToClipboard(user?.email);

	const messageSender = (
		<>
			<ProfileAvatar name={currentMessageOwnerName} picture={picture} />
			<Box xcss={styles.nameContainer}>{currentMessageOwnerName}</Box>
		</>
	);

	const externalMessageIntegration = useMemo(
		() =>
			getExternalMessageDmIntegrationInfo(integrationId, {
				email: user?.email,
				externalId: user?.externalId,
			}),
		[integrationId, user?.email, user?.externalId],
	);

	return (
		<Box xcss={styles.threadItem}>
			<Text maxLines={serveDoubleLineRenderingModifications ? 2 : 1}>
				{userData ? (
					// if we don't have enough data to render the person card we shouldn't make the name
					//	a popup trigger
					<Popup
						isOpen={isPersonPopupOpen}
						onClose={() => setIsPersonPopupOpen(false)}
						content={() =>
							userData ? (
								<PersonCard
									user={userData}
									externalMessageIntegration={externalMessageIntegration}
									onCopyEmail={onCopyEmail}
								/>
							) : null
						}
						trigger={(triggerProps) => (
							<Pressable
								xcss={styles.popupTrigger}
								onClick={() => setIsPersonPopupOpen(true)}
								{...triggerProps}
							>
								{messageSender}
							</Pressable>
						)}
					/>
				) : (
					<Box xcss={styles.regularUser}>{messageSender}</Box>
				)}

				<MessageBody integrationId={integrationId} node={data} />
			</Text>
		</Box>
	);
};
